import { leafletLayer, type Feature, type PaintRule } from "protomaps-leaflet";
import { FeatureGroup, useMap } from "react-leaflet";
import { useCallback, useState, useEffect, useRef } from "react";
import { type GridLayer, type LayersControlEvent } from "leaflet";
import { LAYER_Z_INDEX } from "@/features/map/constants/layerZIndex";
import { useProtomapsPrintAdapter } from "@/features/map/hooks/useProtomapsPrintAdapter";
import { shiftPaintRuleZoom } from "@/features/map/utils/protomapsPrintAdapter";
import type { PrintRenderContext } from "@/features/map/utils/printRenderScale";

/*
ベクターデータの描画スタイルをカスタマイズするためのクラス
https://docs.protomaps.com/pmtiles/leaflet
*/
export class CustomSymbolizer {
  attributeKey: string;
  colorMap: Record<string, string>;
  filterKey?: string;
  filterValues?: string[];
  opacity: number;

  constructor(
    attributeKey: string,
    colorMap: Record<string | number, string>,
    filterKey?: string,
    filterValues?: string[],
    opacity = 0.7
  ) {
    this.attributeKey = attributeKey;
    // Record<number, string> を受け取っても内部では文字列キーで扱う
    this.colorMap = Object.entries(colorMap).reduce<Record<string, string>>(
      (acc, [key, value]) => {
        acc[key] = value;
        return acc;
      },
      {}
    );
    this.filterKey = filterKey;
    this.filterValues = filterValues;
    this.opacity = opacity;
  }

  draw(
    ctx: CanvasRenderingContext2D,
    geom: Feature["geom"],
    z: number,
    feature: Feature
  ): void {
    const attributes = feature.props;

    if (this.filterKey && this.filterValues && this.filterValues.length > 0) {
      const filterValue = attributes[this.filterKey];
      const filterKeyString =
        filterValue !== undefined && filterValue !== null
          ? String(filterValue)
          : "";
      if (!this.filterValues.includes(filterKeyString)) {
        return;
      }
    }

    const value = attributes[this.attributeKey];
    const colorKey = value !== undefined && value !== null ? String(value) : "";
    ctx.globalAlpha = this.opacity;
    const fillColor = this.colorMap[colorKey] || "rgba(0,0,0,0)";
    ctx.fillStyle = fillColor;

    ctx.beginPath();
    for (const poly of geom) {
      for (let p = 0; p < poly.length - 1; p++) {
        const pt = poly[p];
        if (p === 0) ctx.moveTo(pt.x, pt.y);
        else ctx.lineTo(pt.x, pt.y);
      }
    }
    ctx.fill();
  }
}

interface Props {
  defaultVisible: boolean;
  layerName: string;
  url: string;
  attributeKey: string;
  colorMap: Record<string | number, string>;
  paintRules?: PaintRule[];
  dataLayer?: string;
  minzoom?: number;
  maxzoom?: number;
  visible?: boolean;
  filterKey?: string;
  filterValues?: string[];
  zIndex?: number;
  opacity?: number;
}

export const HAZARD_BASE_MIN_ZOOM = 14;
export const HAZARD_BASE_MAX_ZOOM = 22;

interface HazardBaseRuleOptions {
  dataLayer: string;
  attributeKey: string;
  colorMap: Record<string | number, string>;
  paintRules?: PaintRule[];
  minzoom?: number;
  maxzoom?: number;
  filterKey?: string;
  filterValues?: string[];
}

export const buildHazardBasePaintRules = (
  {
    dataLayer,
    attributeKey,
    colorMap,
    paintRules,
    minzoom,
    maxzoom,
    filterKey,
    filterValues,
  }: HazardBaseRuleOptions,
  _renderScale: number,
  zoomOffset: number
): PaintRule[] => {
  const rules = paintRules ?? [
    {
      dataLayer,
      symbolizer: new CustomSymbolizer(
        attributeKey,
        colorMap,
        filterKey,
        filterValues
      ),
      maxzoom,
      minzoom,
    },
  ];

  return rules.map((rule) => shiftPaintRuleZoom(rule, zoomOffset));
};

/*
pmtilesを利用したハザードマップのベースレイヤー

*/
export const HazardBaseLayer: React.FC<Props> = ({
  defaultVisible,
  layerName,
  url,
  attributeKey,
  colorMap,
  paintRules,
  dataLayer = "4326_merged",
  minzoom = HAZARD_BASE_MIN_ZOOM,
  maxzoom = HAZARD_BASE_MAX_ZOOM,
  visible: visibleProp,
  filterKey,
  filterValues,
  zIndex = LAYER_Z_INDEX.HAZARD,
  opacity,
}) => {
  const map = useMap();
  const [visible, setVisible] = useState(defaultVisible);
  const layerRef = useRef<GridLayer | null>(null);
  const createPrintLayer = useCallback(
    (context: PrintRenderContext): GridLayer => {
      const layer = leafletLayer({
        url,
        paintRules: buildHazardBasePaintRules(
          {
            dataLayer,
            attributeKey,
            colorMap,
            paintRules,
            minzoom,
            maxzoom,
            filterKey,
            filterValues,
          },
          context.printScale,
          context.zoomOffset
        ),
        attribution:
          '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>',
      }) as GridLayer;
      if (opacity !== undefined) {
        layer.setOpacity(opacity);
      }
      return layer;
    },
    [
      url,
      dataLayer,
      attributeKey,
      colorMap,
      paintRules,
      minzoom,
      maxzoom,
      filterKey,
      filterValues,
      opacity,
    ]
  );

  useProtomapsPrintAdapter({
    map,
    enabled: visible,
    normalLayerRef: layerRef,
    createPrintLayer,
  });

  // 外部からの制御がある場合はそれを優先し、なければLayersControlイベントで制御
  useEffect(() => {
    if (!map) return;

    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
      return;
    }

    const handleOverlayAdd = (e: LayersControlEvent): void => {
      if (e.name === layerName) {
        setVisible(true);
      }
    };

    const handleOverlayRemove = (e: LayersControlEvent): void => {
      if (e.name === layerName) {
        setVisible(false);
      }
    };

    map.on("overlayadd", handleOverlayAdd);
    map.on("overlayremove", handleOverlayRemove);

    return () => {
      map.off("overlayadd", handleOverlayAdd);
      map.off("overlayremove", handleOverlayRemove);
    };
  }, [map, layerName, visibleProp]);

  useEffect(() => {
    if (typeof visibleProp === "boolean") {
      setVisible(visibleProp);
    }
  }, [visibleProp]);

  useEffect(() => {
    if (layerRef.current) {
      if (visible) {
        layerRef.current.addTo(map);
      } else {
        map.removeLayer(layerRef.current);
      }
    }
  }, [visible, map]);

  useEffect(() => {
    if (!map) return;

    const resolvedPaintRules = buildHazardBasePaintRules(
      {
        dataLayer,
        attributeKey,
        colorMap,
        paintRules,
        minzoom,
        maxzoom,
        filterKey,
        filterValues,
      },
      1,
      0
    );

    const layer = leafletLayer({
      url,
      paintRules: resolvedPaintRules,
      attribution:
        '<a href="https://nlftp.mlit.go.jp/ksj/other/agreement.html">国土数値情報</a>',
    }) as GridLayer;

    layer.setZIndex(zIndex);
    if (opacity !== undefined) {
      layer.setOpacity(opacity);
    }

    layerRef.current = layer;
    if (visible) {
      layer.addTo(map);
    }

    return () => {
      if (map && layerRef.current) {
        map.removeLayer(layerRef.current);
      }
    };
  }, [
    map,
    url,
    minzoom,
    maxzoom,
    zIndex,
    opacity,
    paintRules,
    dataLayer,
    attributeKey,
    colorMap,
    filterKey,
    filterValues,
  ]);

  return <FeatureGroup />;
};
